/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { FC, useMemo, useCallback } from "react";

// antd:
import { Tabs } from "antd";

// eslint-disable-next-line node/no-missing-import
import type * as CSS from "csstype";

// My Components:
import AppIcons from "../react/AppIcons.tsx";
import SpanE from "../react/SpanE.tsx";
// Redux-hooks:
import { useAppSelector, useAppDispatch } from "../../libs/redux-hooks.ts";
// Reducers:
import { getTheme, getLng, getDict, getUser, getAppState, changeAppState, getSafePtr } from "../../libs/reducer.ts";
// My functions
import fnc from "../../libs/fnc.ts";
// Types:
import type {TuniversalElementProps} from "../../types/Iuigen.d.ts";
import type {Tpermissions} from "../../types/easyTypes.ts";

const { rz } = window;

type Ttabs = {
  type:"card" | "line",
  tabPosition:"top" | "left" | "right" | "bottom",
  size:"middle" | "large" | "small",
  animated:boolean
}

type TtabsScroll = {
  scrollNav:boolean,
  scrollContent:boolean,
  navMaxHeight?:string,
  contentMaxHeight?:string
}

// Позволяем прокидывать CSS-переменные в style, чтобы управлять высотой
// scroll-зон из XML без жесткой привязки к одному экрану.
type TtabsRootStyle = CSS.Properties & {
  ["--rz-tabs-nav-max-height"]?:string,
  ["--rz-tabs-content-max-height"]?:string
}

const RZtabs:FC<TuniversalElementProps> = ({attributes}) => { // children
  RZtabs.displayName = "Tabs";
  const dispatch = useAppDispatch();
  const user = useAppSelector(getUser);
  const theme = useAppSelector(getTheme);
  const dct = useAppSelector(getDict);
  const lng = useAppSelector(getLng);
  const safe = useAppSelector(getSafePtr);
  const rlng = (lng === "en" ? "ru" : "en");

  const {rootAttributes, key, fullPath, type, content} = attributes; // onClick, onChange, onRightClick, onHover, onLoad
  const appState = useAppSelector(getAppState(rootAttributes.key)), elState = appState[fullPath];
  const {color, backgroundColor} = elState.attributes;
  const rootNamespace = useMemo(() => (window[rootAttributes?.key || rz.strMakeID(24)]), [rootAttributes?.key]);

  const permissions = useMemo<Tpermissions>(() => (fnc.checkPermissions(user, attributes)), [user, attributes]);

  const clsu = useMemo<Array<string>>(() => {
    const userClss = rz.objClone(elState?.userClass || []);
    // if ((elState?.disabled || permissions.disabled === true) && !~userClss.indexOf("uk-disabled")) { userClss.push("uk-disabled") }
    return userClss;
  }, [elState?.userClass]);

  // Читаем флаги и размеры прокрутки из XML/state.
  // Это нужно для случаев, когда у tabs должны отдельно скроллиться:
  // 1) левая/правая навигация по табам,
  // 2) контент активной вкладки.
  const scrollCfg = useMemo<TtabsScroll>(() => {
    const src = {...attributes, ...elState?.attributes};
    const toBool = (val) => (val === true || val === "true");
    const navMaxHeight = src?.navMaxHeight || src?.navmaxheight;
    const contentMaxHeight = src?.contentMaxHeight || src?.contentmaxheight || navMaxHeight;

    return {
      scrollNav: toBool(src?.scrollNav || src?.scrollnav),
      scrollContent: toBool(src?.scrollContent || src?.scrollcontent),
      navMaxHeight,
      contentMaxHeight
    };
  }, [attributes, elState?.attributes]);

  const styl = useMemo<CSS.Properties>(() => {
    const est = {...elState.userStyle};
    if ((elState?.visible === false || permissions.visible === false) && est.display !== "none") { est.display = "none" }
    else if ((elState?.visible === true && permissions.visible !== false) && est.display === "none") { est.display = undefined }
    let clr:string; const allc = (color || "").split("|"); if (allc.length > 1) { clr = (theme === "dark" ? allc[1] : allc[0]) } else { clr = allc[0] }
    if (!est?.color && clr) { est.color = clr }
    let bclr:string; const allb = (backgroundColor || "").split("|"); if (allb.length > 1) { bclr = (theme === "dark" ? allb[1] : allb[0]) } else { bclr = allb[0] }
    if (!est?.backgroundColor && bclr) { est.backgroundColor = bclr }
    return est;
  }, [elState?.userStyle, elState?.visible, color, backgroundColor, theme, permissions]);

  // Прокидываем размеры в CSS variables, чтобы стили могли ограничить
  // высоту nav-области и контента без ручного вычисления в SCSS.
  const tabsRootStyle = useMemo<TtabsRootStyle>(() => {
    const est:TtabsRootStyle = {};
    if (scrollCfg.navMaxHeight) { est["--rz-tabs-nav-max-height"] = scrollCfg.navMaxHeight }
    if (scrollCfg.contentMaxHeight) { est["--rz-tabs-content-max-height"] = scrollCfg.contentMaxHeight }
    return est;
  }, [scrollCfg]);

  // Добавляем служебные классы только когда скролл явно включен в XML.
  // Так новая логика не влияет на остальные tabs в проекте.
  const tabsClassName = useMemo(() => (
    [
      ...clsu,
      scrollCfg.scrollNav ? "rz-tabs-scroll-nav" : undefined,
      scrollCfg.scrollContent ? "rz-tabs-scroll-content" : undefined
    ].filter(Boolean).join(" ")
  ), [clsu, scrollCfg.scrollNav, scrollCfg.scrollContent]);

  // if (rootNamespace && nextEl.attributes.onChange && rootNamespace[nextEl.attributes.onChange]) { fltSt.onChange = rootNamespace[nextEl.attributes.onChange] }

  const tabsType = useMemo<Ttabs>(() => {
    const typ:Ttabs = {type: "card", tabPosition: "top", size: "middle", animated: false};
    (type || "").replace((/(\s+|,|\|)/igm), ";").split(";").forEach((tt) => {
      if (~["card", "line"].indexOf(tt)) { typ.type = tt }
      else if (~["top", "left", "right", "bottom"].indexOf(tt)) { typ.tabPosition = tt }
      else if (~["middle", "large", "small", "middlesize", "largesize", "smallsize"].indexOf(tt)) { typ.size = tt.replace("size", "") }
      else if (tt === "animated") { typ.animated = true }
    });

    if (attributes?.tabPosition) { typ.tabPosition = attributes.tabPosition }
    if (attributes?.tabtype) { typ.type = attributes.tabtype }
    if (attributes?.animated === "true") { typ.animated = true }
    if (attributes?.size) { typ.size = attributes.size }
    return typ;
  }, [attributes, type]);

  const isGroupTab = useCallback((tabAttributes) => {
    const tabType = (tabAttributes?.type || tabAttributes?.role || "").toString().toLowerCase();
    return Boolean(tabAttributes?.group === "true" || ~["group", "divider", "section"].indexOf(tabType));
  }, []);

  const activeTabChange = useCallback((activeTabKey) => {
    if (fnc.config?.isDev) console.log({action: "<Tabs key='" + key + "'> -> activeTabChange()", activeTabKey});
    dispatch(changeAppState({appkey: rootAttributes.key, fullPath, newState: {...elState, value: activeTabKey}}));
    if (rootNamespace) {
      const clientFNC = rootNamespace[attributes?.onChange || attributes?.onchange];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, activeTabKey, safe}).then((ret) => {
          if (rz.isUndefinedOrNull(ret)) { return }
          if (ret?.err) { console.log(ret.err) }
          if (ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
        });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, activeTabKey, safe}); if (rz.isUndefinedOrNull(ret)) { return }
        if (ret?.err) { console.log(ret.err) }
        if (ret?.state?.fullPath && ret?.state?.newState) {
          dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
        }
      }
    }
  }, [key, dispatch, rootAttributes.key, fullPath, elState, rootNamespace, attributes, user, lng, dct, safe]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tooltip = useMemo<string | undefined>(() => (elState.attributes["tooltip_" + lng] || elState.attributes.tooltip || elState.attributes["tooltip_" + rlng]), [elState.attributes, lng]);

  return (
    <Tabs
      key={key} id={key} className={tabsClassName} style={tabsRootStyle} uk-tooltip={tooltip} tabBarStyle={styl}
      activeKey={elState?.value || attributes?.value || content[0].key || "1"}
      type={tabsType.type}
      tabPosition={tabsType.tabPosition}
      animated={tabsType.animated}
      size={tabsType.size}
      onChange={activeTabChange}
      hidden={elState?.visible === false || permissions.visible === false}
      items={
        content.map((tt) => {
          const perms = fnc.checkPermissions(user, {...tt?.attributes, rootAttributes: {key: rootAttributes.key}});
          const tabState = appState[tt.fullPath];
          const groupTab = isGroupTab(tabState.attributes);
          const tabStyle = (tabState.attributes?.style ? rz.parseCSSText(tabState.attributes?.style) : {});
          // Improved
          if ((tabState?.visible === false || perms.visible === false) && tabStyle.display !== "none") { tabStyle.display = "none" }
          else if ((tabState?.visible === true && perms.visible !== false) && tabStyle.display === "none") { tabStyle.display = undefined }

          if (tabStyle.display === "none") { return null }

          return {
            key: tt.key,
            className: [tabState.attributes?.class, groupTab ? "rz-tabs-nav-group-tab" : undefined].filter(Boolean).join(" ") || undefined,
            style: tabStyle,
            label: (
              <SpanE
                key={tt.key + "-span"}
                className={groupTab ? "rz-tabs-nav-group-label" : undefined}
                uk-tooltip={tabState.attributes["tooltip_" + lng] || tabState.attributes.tooltip || tabState.attributes["tooltip_" + rlng]}
              >
                {tabState.attributes?.icon
                  ? (<AppIcons key={tt.key + "-ico"} icopath={tabState.attributes.icon}
                      attrs={{
                        size: (tabState.attributes?.iconSize ? tabState.attributes.iconSize : undefined),
                        color: tabState.attributes?.iconColor || "black|white",
                        className: tabState.attributes?.iconClass || "rz-margin-right-10"
                      }} />)
                  : undefined
                }
                {tabState.attributes?.img
                  ? (<img key={tt.key + "-img"} src={tabState.attributes.img}
                      style={tabState.attributes.imgStyle ? rz.parseCSSText(tabState.attributes?.imgStyle) : undefined}
                      className={tabState.attributes.imgClass ? tabState.attributes?.imgClass : undefined}
                    />)
                  : undefined
                }
                {tabState.attributes["title_" + lng] || tabState.attributes.title || tabState.attributes["title_" + rlng] || tt.attributes["title_" + lng] || tt.attributes.title || tt.attributes["title_" + rlng]}
              </SpanE>
            ),
            disabled: (groupTab || tabState?.disabled || perms.disabled === true),
            hidden: (tabState?.visible === false || perms.visible === false),
            children: (
              <div
                key={tt.key + "-fragment"}
                // Отдельная обертка нужна, чтобы скроллился именно контент tab-pane,
                // а не весь Tabs целиком вместе с nav-частью.
                className={scrollCfg.scrollContent ? "rz-tabs-scroll-content-inner" : undefined}
              >
                {(tt.content || []).map((el) => (el.children))}
              </div>
            )
          }
        }).filter((tt) => (tt))
      }
    />
  );
}

export default RZtabs;
